import { useStacklineTheme } from '@stackline/ui';
import React, { useCallback, useMemo, memo } from 'react';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import TopEntityChartV2, {
  TopEntityV2ChartProps
} from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { useAppSelector } from 'src/utils/Hooks';
import { getAppName } from 'src/utils/app';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import convertBarChartSeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/barChart';
import { Series, SeriesColumnOptions } from 'highcharts';
import saveAs from 'file-saver';
import { AggregationField } from 'src/types/application/types';
import { EntityCategoryDatum } from 'src/components/BeaconV2Components/TopEntityChartV2/types';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import useAtlasV2DorisColumnData from 'src/serverProxy/useAtlasV2DorisColumnData';

export interface AtlasV2DorisColumnChartProps {
  beaconChartWithLegendPropsBuilder?: (
    props: Omit<BeaconChartWithLegendProps, 'children'>
  ) => Omit<BeaconChartWithLegendProps, 'children'>;
  /**
   * Type of metric to fetch (totalClicks, organicClicks, adClicks, otherClicks, etc.) <- this can be expandable
   */
  metricType: string;
  /**
   * Field to group data by (e.g., 'keyword') - I know this is tricky for exisiting Engs, but BE is now changed the fieldName to use groupByFieldName
   */
  fieldName: string;
  /**
   * Unique query key for caching
   */
  queryKey: string;
  /**
   * Endpoint to fetch data from.
   */
  endPoint: string;
  /**
   * Whether to calculate market share percentages
   */
  showMarketShare?: boolean;
  /**
   * The display type for the metric (VOLUME, PERCENT, MONEY, etc.)
   */
  metricTypeDisplay?: string;
  /**
   * Format the x-axis labels by providing a custom formatter
   */
  xAxisFormatter?: (value: EntityCategoryDatum, id?: string, tooltipId?: string) => string;
  /**
   * Post-process data before mapping it to the chart series but after the default mapping behavior.
   */
  postProcessData?: (data: any, field?: AggregationField, fieldName?: string) => any;
  /**
   * Pre-process data before mapping it to the chart series.
   * The second argument is the field object for the chart.
   */
  preProcessData?: (data: any, field: AggregationField) => any;
  /**
   * Any additional request parameters
   */
  additionalParams?: Record<string, any>;
  /**
   * Maximum number of results to return
   */
  resultCount?: number;
  /**
   * Optional list of keywords to include
   */
  keywords?: string[];
  /**
   * Extra loading state that can be set from outside the component
   */
  isExtraLoading?: boolean;
  /**
   * The name of the index that contains the field information
   */
  indexName: string;
  /**
   * Optional custom function to calculate market share values
   * If provided, this will be used instead of the default market share calculation (default :: fetch it from first request, pass keyword list into second request)
   */
  marketShareCalculator?: (value: number) => number;
  /**
   * Optional chart context ID for registering with the chart context store.
   * When provided, TopEntityChartV2 will register its Highcharts options
   * under this ID so they are included in the Ask Advisor payload.
   * Use constants from chartContextRegistry.ts (e.g. CHART_NAMES.TRAFFIC_BY_KEYWORD_COLUMN).
   */
  chartContextId?: string;
}

/**
 * Shows a column chart that fetches data from Doris endpoint
 * Uses useDorisApplyEntityFilters internally to respect entity filters automatically
 */
export default memo(function AtlasV2DorisColumnChart({
  beaconChartWithLegendPropsBuilder,
  metricType,
  fieldName,
  queryKey,
  endPoint,
  showMarketShare = false,
  metricTypeDisplay,
  xAxisFormatter,
  postProcessData,
  preProcessData,
  additionalParams,
  resultCount,
  keywords,
  isExtraLoading = false,
  indexName,
  marketShareCalculator,
  chartContextId
}: AtlasV2DorisColumnChartProps) {
  const theme = useStacklineTheme();
  const { displayName: mainTimeDisplayName } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonTimeDisplayName } = useAppSelector((state) => state.comparisonTimePeriod);
  const retailer = useAppSelector((state) => state.retailer);

  const { data, isLoading, error } = useAtlasV2DorisColumnData({
    metricType,
    fieldName,
    queryKey,
    endPoint,
    showMarketShare,
    additionalParams,
    resultCount,
    keywords,
    marketShareCalculator
  });

  const field = useMemo(() => INDEX_FIELDS.getField(getAppName(), indexName, metricType), [indexName, metricType]);

  const effectiveMetricType = useMemo(() => {
    return metricTypeDisplay || field.metricType;
  }, [metricTypeDisplay, field.metricType]);

  // Preprocess data before mapping it to the chart series
  const preprocessedData = preProcessData ? preProcessData(data, field) : data;

  // Prepare category data for the chart first

  const categories = preprocessedData.map((row) => {
    return {
      name: row.metadata.name || row.fieldId,
      id: row.fieldId,
      type: fieldName,
      searchTerm: row.metadata.searchTerm || row.fieldId
    };
  });

  const mappedPrimaryData = preprocessedData.map((row) => [row.metadata.name || row.fieldId, row.values[fieldName]]);
  const mappedComparisonData = preprocessedData.map((row) => {
    return [row.metadata.name || row.fieldId, row.comparisonValues[fieldName]];
  });

  // Apply post-processing if needed
  const primaryData = postProcessData ? postProcessData(mappedPrimaryData, field, fieldName) : mappedPrimaryData;
  const comparisonData = postProcessData
    ? postProcessData(mappedComparisonData, field, fieldName)
    : mappedComparisonData;

  // Function to convert chart data to CSV format for download
  const convertSeriesToDelimitedData = useCallback(() => {
    try {
      const csvData = convertBarChartSeriesToDelimitedData(
        [
          {
            type: 'column',
            color: theme.colors.secondary,
            data: comparisonData,
            metricType: effectiveMetricType,
            timePeriod: comparisonTimeDisplayName,
            name: field.displayName,
            categories
          } as unknown as Series,
          {
            type: 'column',
            color: theme.colors.primary,
            data: primaryData,
            metricType: effectiveMetricType,
            timePeriod: mainTimeDisplayName,
            name: field.displayName,
            categories
          } as unknown as Series
        ],
        retailer
      );
      const blob = new Blob([csvData], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, 'chart_data.csv');
    } catch (e) {
      console.error('error: ', e);
    }
    return Promise.resolve();
  }, [
    categories,
    comparisonData,
    comparisonTimeDisplayName,
    effectiveMetricType,
    field.displayName,
    mainTimeDisplayName,
    primaryData,
    retailer,
    theme.colors.primary,
    theme.colors.secondary
  ]);

  // Build props for the BeaconChartWithLegend component
  const getBeaconChartWithLegendProps = useCallback(() => {
    const defaultProps: Omit<BeaconChartWithLegendProps, 'children'> = {
      title: '',
      primaryLegendProps: {
        displayName: mainTimeDisplayName
      },
      comparisonLegendProps: {
        displayName: comparisonTimeDisplayName
      },
      loading: isLoading || isExtraLoading,
      loadingComponent: <SplineChartLoading />,
      convertSeriesToDelimitedData
    };

    if (beaconChartWithLegendPropsBuilder) {
      return beaconChartWithLegendPropsBuilder(defaultProps);
    }

    return defaultProps;
  }, [
    beaconChartWithLegendPropsBuilder,
    comparisonTimeDisplayName,
    convertSeriesToDelimitedData,
    isExtraLoading,
    isLoading,
    mainTimeDisplayName
  ]);

  // Build props for the TopEntityChartV2 component
  const getTopEntityChartV2Props = useCallback(() => {
    const defaultProps: TopEntityV2ChartProps = {
      chartSeries: [
        {
          type: 'column',
          color: theme.colors.secondary,
          data: comparisonData,
          metricType: effectiveMetricType,
          categories
        } as SeriesColumnOptions,
        {
          type: 'column',
          color: theme.colors.primary,
          data: primaryData,
          metricType: effectiveMetricType,
          categories
        } as SeriesColumnOptions
      ],
      xAxisFormatter,
      chartContextId
    };

    return defaultProps;
  }, [
    theme.colors.secondary,
    theme.colors.primary,
    comparisonData,
    effectiveMetricType,
    categories,
    primaryData,
    xAxisFormatter,
    chartContextId
  ]);

  const beaconChartWithLegendProps = useMemo(() => getBeaconChartWithLegendProps(), [getBeaconChartWithLegendProps]);
  const topEntityChartV2Props = useMemo(() => getTopEntityChartV2Props(), [getTopEntityChartV2Props]);

  const noSeriesData = !primaryData.length && !comparisonData.length;

  if (error) {
    return (
      <BeaconChartWithLegend {...beaconChartWithLegendProps}>
        <NoDataPlaceHolder
          title="Error loading data"
          description="There was an error loading the data. Please try again later."
          flexProps={{ margin: '80px auto' }}
        />
      </BeaconChartWithLegend>
    );
  }

  return (
    <BeaconChartWithLegend {...beaconChartWithLegendProps}>
      {noSeriesData && !isLoading ? (
        <NoDataPlaceHolder
          title="Data currently unavailable"
          description="There is currently no data matching your selected criteria. Please try another query to view insights."
          flexProps={{ margin: '80px auto' }}
        />
      ) : (
        <TopEntityChartV2 {...topEntityChartV2Props} />
      )}
    </BeaconChartWithLegend>
  );
});
